/**
 * Il pannello sul telefono: un foglio che sale dal basso sopra la mappa. Chiuso mostra una riga (il riepilogo o
 * l'azienda scelta) e la maniglia; aperto arriva a tre quarti dello schermo e scorre. Si apre e si chiude toccando la
 * maniglia o trascinandola in su e in giù.
 */
import React, { useRef } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { FOGLIO_CHIUSO } from './aspetto';

interface Props {
  aperto: boolean;
  onCambia: (aperto: boolean) => void;
  anteprima: React.ReactNode;
  children: React.ReactNode;
}

const SOGLIA_TRASCINAMENTO = 24;

export const FoglioMobile: React.FC<Props> = ({ aperto, onCambia, anteprima, children }) => {
  const { t } = useTranslation('mappa');
  const partenza = useRef<number | null>(null);
  const trascinato = useRef(false);

  return (
    <Box
      component="section"
      aria-label={aperto ? t('pannello.riduci') : t('pannello.apri')}
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: aperto ? 'min(72%, 640px)' : `calc(${FOGLIO_CHIUSO}px + env(safe-area-inset-bottom))`,
        transition: 'height 280ms cubic-bezier(.2,.8,.2,1)',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        bgcolor: tokens.color.paper,
        borderTop: `1px solid ${tokens.color.line}`,
        borderRadius: '18px 18px 0 0',
        boxShadow: '0 -8px 28px rgba(22,22,22,0.08)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 2,
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      <ButtonBase
        onClick={() => {
          if (trascinato.current) { trascinato.current = false; return; }
          onCambia(!aperto);
        }}
        onPointerDown={(e) => { partenza.current = e.clientY; trascinato.current = false; }}
        onPointerMove={(e) => {
          if (partenza.current === null) return;
          const delta = e.clientY - partenza.current;
          if (Math.abs(delta) < SOGLIA_TRASCINAMENTO) return;
          trascinato.current = true;
          partenza.current = null;
          onCambia(delta < 0);
        }}
        onPointerUp={() => { partenza.current = null; }}
        aria-expanded={aperto}
        aria-label={aperto ? t('pannello.riduci') : t('pannello.apri')}
        sx={{ width: '100%', flexDirection: 'column', alignItems: 'stretch', touchAction: 'none', textAlign: 'left', flexShrink: 0 }}
      >
        <Box sx={{ alignSelf: 'center', width: 40, height: 4, borderRadius: 2, bgcolor: tokens.color.line, mt: 1, mb: 0.5 }} />
        {!aperto && <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>{anteprima}</Box>}
      </ButtonBase>
      <Box sx={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', display: aperto ? 'block' : 'none' }}>
        {children}
      </Box>
    </Box>
  );
};
